/**
 * Lightweight markdown-to-HTML renderer for chat messages.
 *
 * Supports: ## h4, ### h5, **bold**, *italic*, `code`,
 * [text](url), - unordered lists, 1. ordered lists, paragraphs.
 *
 * Separate file from TextBlock's simpleMarkdown to avoid coupling
 * chat rendering with report block rendering.
 */

function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function inlineFormat(text) {
    let result = escapeHtml(text);
    // **bold**
    result = result.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // *italic* (but not inside already-matched **)
    result = result.replace(/\*(.+?)\*/g, '<em>$1</em>');
    // `code`
    result = result.replace(/`([^`]+)`/g, '<code>$1</code>');
    // [text](url)
    result = result.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    return result;
}

export function renderMarkdown(text) {
    if (!text) return '';

    const lines = text.split('\n');
    const htmlParts = [];
    let inUl = false;
    let inOl = false;
    let currentParagraph = [];

    const flushParagraph = () => {
        if (currentParagraph.length > 0) {
            const content = currentParagraph.join(' ').trim();
            if (content) {
                htmlParts.push(`<p>${inlineFormat(content)}</p>`);
            }
            currentParagraph = [];
        }
    };

    const flushList = () => {
        if (inUl) {
            htmlParts.push('</ul>');
            inUl = false;
        }
        if (inOl) {
            htmlParts.push('</ol>');
            inOl = false;
        }
    };

    for (const line of lines) {
        const trimmed = line.trim();

        // Empty line — flush
        if (trimmed === '') {
            flushParagraph();
            flushList();
            continue;
        }

        // ### Heading -> h5
        if (trimmed.startsWith('### ')) {
            flushParagraph();
            flushList();
            htmlParts.push(`<h5>${inlineFormat(trimmed.slice(4))}</h5>`);
            continue;
        }

        // ## Heading -> h4
        if (trimmed.startsWith('## ')) {
            flushParagraph();
            flushList();
            htmlParts.push(`<h4>${inlineFormat(trimmed.slice(3))}</h4>`);
            continue;
        }

        // - unordered list item
        if (trimmed.startsWith('- ')) {
            flushParagraph();
            if (inOl) { flushList(); }
            if (!inUl) {
                htmlParts.push('<ul>');
                inUl = true;
            }
            htmlParts.push(`<li>${inlineFormat(trimmed.slice(2))}</li>`);
            continue;
        }

        // 1. ordered list item
        const olMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
        if (olMatch) {
            flushParagraph();
            if (inUl) { flushList(); }
            if (!inOl) {
                htmlParts.push('<ol>');
                inOl = true;
            }
            htmlParts.push(`<li>${inlineFormat(olMatch[2])}</li>`);
            continue;
        }

        // Regular text — accumulate for paragraph
        if (inUl || inOl) {
            flushList();
        }
        currentParagraph.push(trimmed);
    }

    flushParagraph();
    flushList();

    return htmlParts.join('');
}
